import React, { useState } from 'react';
import {
  View, Text, StyleSheet, Pressable,
} from 'react-native';
import Moment from 'moment';
import { FontAwesome } from '@expo/vector-icons';
import Colors from '../../../../constants/Colors';
import AddEditNote from '../../../AddEditNote';
import Button from '../../../Button';
import PlusIcon from '../../../16pxIcons/PlusIcon';
import Style from '../../../../constants/Style';
import { AssetNote } from '../../../../constants/Types';

interface NoteSectionProps {
  notesArray: AssetNote[],
  id: string,
  name: string,
}

const NoteSection = ({
  notesArray, id, name,
}: NoteSectionProps) => {
  const [addNoteModalVisible, setAddNoteModalVisible] = useState<boolean>(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [editNote, setEditNote] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [notes, setNotes] = useState<AssetNote[]>(notesArray);

  const pageCount: number = Math.ceil(notes.length / 2) - 1;
  const leftPageDisabled: boolean = pageNumber <= 0;
  const rightPageDisabled: boolean = pageNumber >= pageCount;

  return <View style={styles.container}>
    <Text style={styles.sectionTitle}>{'NOTES'}</Text>
    <View style={styles.underline} />
    <AddEditNote
      modalVisible={addNoteModalVisible}
      {...{
        notes, name, selectedNote, editNote, setNotes,
      }}
      setVisible={() => setAddNoteModalVisible(false)}
      setEditNote={() => setEditNote(true)}
      assetId={id}
    />
    <View style={styles.noteList}>
      {notes.slice(pageNumber * 2, (pageNumber * 2) + 2).map((note, index: number) => <Pressable
        key={index}
        style={{
          justifyContent: 'center',
          marginVertical: 8,
        }}
        onPress={() => {
          setSelectedNote(note);
          setEditNote(false);
          setAddNoteModalVisible(!addNoteModalVisible);
        }}
        testID={`asset-note#${index + 1}`}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <View style={styles.bulletPoint} />
          <Text
            style={styles.title}
            numberOfLines={1}
            ellipsizeMode='tail'
          >
            {note.title}
          </Text>
          <Text style={styles.date}>{Moment(note.updatedOn).format('DD.MM.YY')}</Text>
        </View>
        <Text style={styles.value}>{`by ${note.name}`}</Text>
      </Pressable>)}
    </View>
    <View style={Style.row}>
      <Button
        title='Add note'
        onPress={() => {
          setEditNote(false);
          setSelectedNote(null);
          setAddNoteModalVisible(!addNoteModalVisible);
        }}
        textStyle={styles.addButtonText}
        style={styles.addButton}
        children={<PlusIcon color={Colors.light.hereworksBlue500} />}
      />
      <View style={Style.row}>
        <Pressable
          style={[styles.pageShiftButton, { opacity: leftPageDisabled ? 0.2 : 1 }]}
          onPress={() => setPageNumber(Math.max(0, pageNumber - 1))}
          disabled={leftPageDisabled}
        >
          <FontAwesome
            name='angle-left'
            color={Colors.light.hereworksBlue500}
            size={24}
          />
        </Pressable>
        <Pressable
          style={[styles.pageShiftButton, { opacity: rightPageDisabled ? 0.2 : 1 }]}
          onPress={() => setPageNumber(Math.min(pageCount, pageNumber + 1))}
          disabled={rightPageDisabled}
        >
          <FontAwesome
            name='angle-right'
            color={Colors.light.hereworksBlue500}
            size={24}
          />
        </Pressable>
      </View>
    </View>
  </View>;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginTop: 16,
  },
  sectionTitle: {
    fontSize: 12,
    lineHeight: 18,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack400,
  },
  underline: {
    alignSelf: 'stretch',
    height: 1,
    backgroundColor: Colors.light.hereworksBlue100,
    marginTop: 2,
  },
  noteList: {
    flex: 1,
    marginTop: 8,
    marginBottom: 16,
    height: 100,
  },
  bulletPoint: {
    backgroundColor: Colors.light.hereworksDarkBlue,
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 8,
  },
  title: {
    flex: 1,
    fontFamily: 'Roboto_500Medium',
    fontSize: 12,
    lineHeight: 14,
    color: Colors.light.hereworksBlack600,
  },
  date: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 12,
    lineHeight: 14,
    color: Colors.light.hereworksBlack400,
  },
  value: {
    fontFamily: 'Roboto_400Regular',
    fontSize: 11,
    lineHeight: 17,
    color: Colors.light.hereworksBlack400,
    marginTop: 2,
    marginStart: 16,
  },
  addButton: {
    flex: undefined,
  },
  addButtonText: {
    marginEnd: 8,
  },
  pageShiftButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue500,
    justifyContent: 'center',
    alignItems: 'center',
    marginStart: 12,
  },
});

export default NoteSection;
